import { Price } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import { Title } from '@components';
import { Box, Stack, styled } from '@mui/material';
import { colors } from '@themes/colors';
import { breakpoints } from '@themes/theme.helpers';
import { t } from '@translations';
import { formatPrice } from '@utils/price.utils';
import { ReactNode } from 'react';

// STYLES
//--------

const OrderSummaryWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  background-color: white;
  padding: 16px;
  border-radius: 4px;

  ${breakpoints.up('md')} {
    padding: 40px;
  }

  .main-title {
    padding-bottom: 20px;
  }
`;

const Row = styled(Box)`
  display: flex;
  justify-content: space-between;
  padding: 5px 0;
  gap: 8px;
  &.total {
    font-weight: 700;
    font-size: 24px;
    padding: 20px 0;
  }
`;
const Label = styled(Box)`
  width: 200px;
  ${breakpoints.up('md')} {
    width: auto;
  }
`;
const Value = styled(Box)`
  font-weight: 700;

  &.no-delivery {
    font-weight: 400;
    text-align: right;
  }
`;

const ButtonBox = styled(Stack)`
  margin-top: 20px;
`;

export type OrderSummaryProps = {
  title?: string;
  itemCount?: number | string;
  total?: Price;
  tax?: Price;
  showDeliveryInfo?: boolean;
  delivery?: Price;
  subtotal?: Price;
  buttons?: ReactNode;
  additionalMessage?: string;
};

export const OrderSummary = ({
  itemCount,
  title,
  subtotal,
  total,
  tax,
  delivery,
  buttons,
  additionalMessage,
}: OrderSummaryProps) => {
  return (
    <OrderSummaryWrapper>
      <Title variant='h3'>{title}</Title>
      <Row>
        <Label>{t(TextCodes.Product.Cart.Products)}</Label>
        <Value>{itemCount}</Value>
      </Row>
      <Row>
        <Label>{t(TextCodes.Product.Cart.Subtotal)}</Label>
        <Value>{formatPrice(subtotal)}</Value>
      </Row>
      {delivery?.amount ? (
        <Row>
          <Label>{t(TextCodes.Product.Cart.Delivery)}</Label>
          <Value>{formatPrice(delivery)}</Value>
        </Row>
      ) : null}
      <Row>
        <Label>{t(TextCodes.Product.Cart.Tax)}</Label>
        <Value>{formatPrice(tax)}</Value>
      </Row>
      <Row className='total'>
        <Label>{t(TextCodes.Product.Cart.Total)}</Label>
        <Value>{formatPrice(total)}</Value>
      </Row>
      {!delivery?.amount ? (
        <Row
          sx={{ justifyContent: 'center', color: colors.secondary.darkBlueGrey }}
          fontStyle='italic'
        >
          <Value
            className='no-delivery'
            sx={{ width: '100%' }}
            dangerouslySetInnerHTML={{ __html: t(TextCodes.Product.Cart.DeliveryInfo) }}
          ></Value>
        </Row>
      ) : null}
      {buttons && (
        <ButtonBox spacing={2} direction='row'>
          {buttons}
        </ButtonBox>
      )}
      {additionalMessage ? (
        <Row
          sx={{ justifyContent: 'center', color: colors.secondary.darkBlueGrey }}
          fontStyle='italic'
        >
          <Value
            className='no-delivery'
            sx={{ width: '100%' }}
            dangerouslySetInnerHTML={{ __html: additionalMessage }}
          ></Value>
        </Row>
      ) : null}

    </OrderSummaryWrapper>
  );
};
